import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export const SendDialog = ({ product, disabled }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer_email: "", customer_name: "", order_ref: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/deliveries", { ...f, product_id: product.id });
      setResult(r.data);
      if (r.data.email_status === "sent") toast.success(`Sent to ${r.data.customer_email}`);
      else toast.error(`Link created but email failed: ${r.data.email_error}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = (o) => {
    setOpen(o);
    if (!o) {
      setResult(null);
      setF({ customer_email: "", customer_name: "", order_ref: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button disabled={disabled} className="rounded-none gap-2 w-full bg-[#38171E] hover:bg-[#2A1016] text-[#F3E5AB]" data-testid="send-to-customer-btn"><Send size={15} /> Send to customer</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFDF9] rounded-none" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">Send “{product.title}”</DialogTitle>
          <DialogDescription>The buyer receives an email with a private download link (30 days, up to 10 downloads).</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="send-result">
            <p className="font-body-serif">
              {result.email_status === "sent" ? "Email sent to " : "Email could not be sent to "}
              <strong>{result.customer_email}</strong>.
            </p>
            <div>
              <Label>Download link</Label>
              <div className="flex gap-2 mt-2">
                <Input readOnly value={result.link} data-testid="send-result-link" />
                <Button variant="outline" className="rounded-none" onClick={() => { navigator.clipboard.writeText(result.link); toast.success("Copied"); }} data-testid="send-copy-link-btn">Copy</Button>
              </div>
            </div>
            <Button className="rounded-none w-full" onClick={() => reset(false)} data-testid="send-done-btn">Done</Button>
          </div>
        ) : (
          <form onSubmit={send} className="space-y-4">
            <div>
              <Label>Customer email</Label>
              <Input type="email" required value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} className="mt-2" data-testid="send-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer name</Label>
                <Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className="mt-2" data-testid="send-name-input" />
              </div>
              <div>
                <Label>Order number (optional)</Label>
                <Input value={f.order_ref} onChange={(e) => setF({ ...f, order_ref: e.target.value })} className="mt-2" data-testid="send-order-input" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="rounded-none w-full" data-testid="send-submit-btn">{busy ? "Sending…" : "Send download link"}</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
